import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, of, switchMap } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { UserPreferenceService } from '@core/services/user-preference.service';
import {
  UserWorkspaceContextAccountModel,
  UserWorkspaceContextService,
  UserWorkspaceContextWorkspaceModel,
} from '@core/services/user-workspace-context.service';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';

@Component({
  selector: 'app-switch-workspace-modal',
  imports: [
    FormsModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    NgLabelTemplateDirective,
  ],
  templateUrl: './switch-workspace-modal.html',
  styleUrl: './switch-workspace-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwitchWorkspaceModal {
  readonly activeModal = inject(NgbActiveModal);
  private readonly authService = inject(AuthService);
  private readonly workspaceContextService = inject(
    UserWorkspaceContextService,
  );
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly injector = inject(Injector);

  readonly accounts = signal<UserWorkspaceContextAccountModel[]>([]);
  readonly workspaces = signal<UserWorkspaceContextWorkspaceModel[]>([]);
  readonly loading = signal(true);
  readonly switching = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly switchError = signal<string | null>(null);
  readonly selectedAccountId = signal<number | null>(null);
  readonly selectedWorkspaceId = signal<number | null>(null);

  readonly hasAccounts = computed(() => this.accounts().length > 0);
  readonly hasWorkspaces = computed(() => this.workspaces().length > 0);
  readonly currentAccountId = this.authService.GetAccount();
  readonly currentWorkspaceId = this.authService.GetWorkspace();
  readonly accountSelect = viewChild(NgSelectComponent);
  readonly accountSelectInputAttributes = { ngbAutofocus: '' };
  readonly canSwitch = computed(() => {
    const accountId = this.selectedAccountId();
    const selectedId = this.selectedWorkspaceId();
    return Boolean(
      !this.loading() &&
        !this.switching() &&
        this.hasAccounts() &&
        this.hasWorkspaces() &&
        accountId &&
        accountId > 0 &&
        selectedId &&
        selectedId > 0 &&
        selectedId !== this.currentWorkspaceId,
    );
  });

  constructor() {
    this.loadContexts();
  }

  onAccountChange(accountId: number | null): void {
    this.selectedAccountId.set(accountId);
    this.selectedWorkspaceId.set(null);
    this.workspaces.set(
      this.accounts().find((account) => account.id === accountId)?.workspaces ??
        [],
    );
  }

  onSwitch(): void {
    const workspaceId = this.selectedWorkspaceId();
    if (!workspaceId || !this.canSwitch()) {
      return;
    }

    this.switchError.set(null);
    this.switching.set(true);

    this.authService
      .switchWorkspace(workspaceId)
      .pipe(
        switchMap(() =>
          this.userPreferenceService.get().pipe(
            catchError(() => of(null)),
            switchMap((preference) =>
              this.userPreferenceService.savePatch(preference, {
                lastWorkspaceId: workspaceId,
              }),
            ),
          ),
        ),
        finalize(() => this.switching.set(false)),
      )
      .subscribe({
        next: () => this.activeModal.close({ switched: true }),
        error: (error: Error) => {
          this.switchError.set(
            error?.message ?? 'Nao foi possivel trocar o workspace.',
          );
        },
      });
  }

  private loadContexts(): void {
    this.loading.set(true);
    this.loadError.set(null);

    this.workspaceContextService
      .list()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (accounts) => {
          const sorted = this.sortAccounts(accounts);
          this.accounts.set(sorted);
          const currentAccount = sorted.find(
            (account) => account.id === this.currentAccountId,
          );
          this.selectedAccountId.set(currentAccount?.id ?? null);
          this.workspaces.set(currentAccount?.workspaces ?? []);
          this.selectedWorkspaceId.set(
            currentAccount?.workspaces.some(
              (workspace) => workspace.id === this.currentWorkspaceId,
            )
              ? this.currentWorkspaceId
              : null,
          );
          this.focusAccountSelect();
        },
        error: (error: Error) => {
          this.accounts.set([]);
          this.workspaces.set([]);
          this.loadError.set(
            error?.message ?? 'Nao foi possivel carregar as contas.',
          );
        },
      });
  }

  private focusAccountSelect(): void {
    afterNextRender(() => this.accountSelect()?.focus(), {
      injector: this.injector,
    });
  }

  private sortAccounts(
    accounts: UserWorkspaceContextAccountModel[],
  ): UserWorkspaceContextAccountModel[] {
    return accounts
      .map((account) => ({
        ...account,
        workspaces: [...account.workspaces].sort((left, right) =>
          left.name.localeCompare(right.name),
        ),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }
}
